import type { InstrumentState, InstrumentType } from '@/domain/types';
import type { AutomatableParam } from '../effects/EffectNode';
import { defaultInstrumentParams } from './descriptors';
import { envelopeLevelAt, type EnvelopeShape } from './envelope';
import { crossfadedLoopCopy, usableCrossfade } from './loopCrossfade';
import { MOD_WHEEL_CENTS, rampParam, registerInstrument, type BufferResolver, type InstrumentNode } from './Instrument';
import { chooseZones, readSamplerZones, zonesForNote, type SamplerZone } from './samplerState';

/**
 * The sampler.
 *
 * It plays the user's own audio across the keyboard: each zone maps a region of
 * an imported asset to a range of keys, and a note is transposed by resampling
 * — `playbackRate = 2^(semitones/12)`, which is what a sampler has always done
 * and what the Web Audio buffer source gives for free.
 *
 * The voice architecture is deliberately the synth's:
 *
 *   per voice:  buffer source ─▶ voice VCA (ADSR) ─┐
 *                                                  ▼
 *                                    shared filter ──▶ output gain
 *                                          ▲
 *                          cutoff source ──┤
 *                       filter envelope ───┘
 *
 * Sharing that design is not laziness: it means the two instruments have the
 * same controls doing the same thing, the cutoff remains a single automatable
 * `AudioParam`, and a held chord costs one filter rather than one per note.
 *
 * Audio is fetched through a synchronous resolver. Notes are scheduled on the
 * audio clock and cannot wait for I/O, so a zone whose sample is not decoded
 * yet stays silent for that note rather than delaying every other track.
 */

const MAX_VOICES = 32;
/** How long a choked voice takes to go: short enough to read as a cut, long enough not to click. */
const CHOKE_FADE_SEC = 0.005;

interface Voice {
  source: AudioBufferSourceNode;
  gain: GainNode;
  pitch: number;
  startedAt: number;
  endsAt: number;
  released: boolean;
  /** The zone, kept for a looping voice so the release can find the region's end. */
  zone: SamplerZone | null;
  /** The rest of the region after the loop, started by the release. */
  tail: AudioBufferSourceNode | null;
  /** Ignores the note letting go; ends when its region does. */
  oneShot: boolean;
  /** The choke group this voice belongs to, or zero. */
  chokeGroup: number;
  /** The amplitude envelope as scheduled, so a release can start from where it is. */
  envelope: EnvelopeShape;
}

class Sampler implements InstrumentNode {
  readonly type: InstrumentType = 'sampler';
  readonly output: GainNode;

  private readonly filter: BiquadFilterNode;
  private readonly bendSource: ConstantSourceNode;
  private readonly cutoffSource: ConstantSourceNode;
  private readonly modSource: ConstantSourceNode;
  private readonly envSource: ConstantSourceNode;
  private readonly envAmount: GainNode;
  private readonly voices: Voice[] = [];
  private params: Record<string, number>;
  private zones: SamplerZone[] = [];
  /**
   * Whose turn it is in each round-robin group.
   *
   * Kept on the instrument rather than in the zone, so an offline render and a
   * live performance do not advance each other's turn — and so reloading a
   * project starts from the first take rather than wherever the last session
   * happened to stop.
   */
  private readonly turns = new Map<number, number>();
  /** Reversed copies, built on demand and cached per asset. */
  private reversed = new Map<string, AudioBuffer>();
  private looped = new Map<string, AudioBuffer>();

  constructor(
    private readonly ctx: BaseAudioContext,
    private readonly resolveBuffer: BufferResolver,
  ) {
    this.params = defaultInstrumentParams('sampler');

    this.output = ctx.createGain();
    this.output.gain.value = this.params.volume;

    // Shared across the voices, like the synth's, and in cents for the same
    // reason: `detune` is the one parameter on a buffer source that *adds*.
    // Playback rate is the sampler's usual way of pitching and cannot be used
    // here — a bend has to multiply the rate, and a connection can only sum.
    this.bendSource = ctx.createConstantSource();
    this.bendSource.offset.value = this.params.bend * 100;
    this.bendSource.start();

    this.filter = ctx.createBiquadFilter();
    this.filter.type = 'lowpass';
    this.filter.frequency.value = 0;
    this.filter.Q.value = this.params.resonance;
    this.filter.connect(this.output);

    this.cutoffSource = ctx.createConstantSource();
    this.cutoffSource.offset.value = this.params.cutoff;
    this.cutoffSource.connect(this.filter.frequency);
    this.cutoffSource.start();

    this.modSource = ctx.createConstantSource();
    this.modSource.offset.value = (this.params.modWheel ?? 0) * MOD_WHEEL_CENTS;
    this.modSource.connect(this.filter.detune);
    this.modSource.start();

    this.envSource = ctx.createConstantSource();
    this.envSource.offset.value = 1;
    this.envAmount = ctx.createGain();
    this.envAmount.gain.value = 0;
    this.envSource.connect(this.envAmount).connect(this.filter.frequency);
    this.envSource.start();
  }

  setState(state: InstrumentState): void {
    this.zones = readSamplerZones(state.data);
  }

  setParam(paramId: string, value: number, atTime?: number): void {
    this.params[paramId] = value;
    switch (paramId) {
      case 'bend':
        rampParam(this.bendSource.offset, value * 100, this.ctx, atTime);
        break;
      case 'cutoff':
        rampParam(this.cutoffSource.offset, value, this.ctx, atTime);
        break;
      case 'resonance':
        rampParam(this.filter.Q, value, this.ctx, atTime);
        break;
      case 'modWheel':
        rampParam(this.modSource.offset, value * MOD_WHEEL_CENTS, this.ctx, atTime);
        break;
      case 'volume':
        rampParam(this.output.gain, value, this.ctx, atTime);
        break;
      default:
        // Envelope times and transposition are read when a voice starts.
        break;
    }
  }

  paramTargets(paramId: string): AutomatableParam[] {
    const identity = (v: number): number => v;
    switch (paramId) {
      // Semitones outside, cents inside, exactly as the synth reads it.
      case 'bend':
        return [{ param: this.bendSource.offset, map: (v) => v * 100 }];
      case 'cutoff':
        return [{ param: this.cutoffSource.offset, map: identity }];
      case 'resonance':
        return [{ param: this.filter.Q, map: identity }];
      case 'modWheel':
        return [{ param: this.modSource.offset, map: (v) => v * MOD_WHEEL_CENTS }];
      case 'volume':
        return [{ param: this.output.gain, map: identity }];
      default:
        return [];
    }
  }

  playNote(pitch: number, velocity: number, when: number, duration: number): void {
    // A written note's end is its note-off, and a one-shot ignores that too.
    for (const voice of this.startVoices(pitch, velocity, when)) {
      if (!voice.oneShot) this.releaseVoice(voice, when + Math.max(0.005, duration));
    }
  }

  noteOn(pitch: number, velocity: number, when: number): void {
    this.startVoices(pitch, velocity, when);
  }

  noteOff(pitch: number, when: number): void {
    // Every voice this pitch started is released, because a key can trigger
    // several layered zones at once — except a one-shot's, which plays out
    // however short the note was. Stopping the transport goes through
    // `allNotesOff`, which does not ask.
    for (let i = this.voices.length - 1; i >= 0; i--) {
      const voice = this.voices[i];
      if (voice.pitch === pitch && !voice.released && !voice.oneShot) this.releaseVoice(voice, when);
    }
  }

  allNotesOff(when: number): void {
    for (const voice of [...this.voices]) this.releaseVoice(voice, when, 0.01);
  }

  private startVoices(pitch: number, velocity: number, when: number): Voice[] {
    this.reap(when);
    // Velocity narrows the choice, then round-robin groups decide which of what
    // is left actually sounds.
    const matches = chooseZones(zonesForNote(this.zones, pitch, velocity), this.turns);
    const started: Voice[] = [];

    for (const zone of matches) {
      const voice = this.startZoneVoice(zone, pitch, velocity, when);
      if (voice) started.push(voice);
    }
    return started;
  }

  private startZoneVoice(zone: SamplerZone, pitch: number, velocity: number, when: number): Voice | null {
    const ctx = this.ctx;
    const p = this.params;

    const raw = this.resolveBuffer(zone.assetId);
    if (!raw) return null;
    // A zone in a choke group stops whatever the group has sounding — the
    // closed hat silencing the open one — with a few milliseconds of fade so
    // the cut is not itself a click. Its own earlier voices included.
    if (zone.chokeGroup > 0) {
      for (const voice of [...this.voices]) {
        if (voice.chokeGroup === zone.chokeGroup) this.releaseVoice(voice, when, CHOKE_FADE_SEC);
      }
    }
    const oriented = zone.reverse ? this.reversedBuffer(zone.assetId, raw) : raw;
    const buffer = zone.loop ? this.loopedBuffer(zone, oriented) : oriented;

    if (this.voices.length >= MAX_VOICES) {
      const oldest = this.voices.reduce((a, b) => (a.startedAt <= b.startedAt ? a : b));
      this.releaseVoice(oldest, when, 0.01);
    }

    const gain = ctx.createGain();
    gain.gain.value = 0;
    gain.connect(this.filter);

    const source = ctx.createBufferSource();
    source.buffer = buffer;
    // Resampling: an octave up is twice the rate. Fine tuning rides on top in
    // cents, which is what the `detune` param is measured in.
    // A zone pinned to its root plays as recorded on every key in its range.
    const semitones = (zone.keyTracking ? pitch - zone.rootPitch : 0) + p.transpose;
    source.playbackRate.value = Math.pow(2, semitones / 12);
    try {
      source.detune.value = zone.tuneCents;
      // The bend rides on top of the zone's own fine tuning, which is why both
      // live on `detune` rather than one of them on the rate.
      this.bendSource.connect(source.detune);
    } catch {
      // `detune` is optional on AudioBufferSourceNode in older implementations.
      // The playback rate above already carries the musical transposition, so a
      // sampler without it plays in tune and simply does not bend — which is
      // what it did before, rather than a new failure.
    }
    if (zone.loop) {
      // The attack, from the region's start to the loop, plays once on the way
      // in; the loop holds while the key is down. What follows the loop is the
      // release's, and is heard when the note lets go.
      source.loop = true;
      source.loopStart = zone.loopStartSec;
      source.loopEnd = Math.max(zone.loopStartSec + 0.001, zone.loopEndSec);
    }
    source.connect(gain);

    // Velocity moves the level by as much as the instrument says it should:
    // all of it for a piano, none of it for a kit that hits the same every time.
    const force = Math.max(1, Math.min(127, velocity)) / 127;
    const amount = Math.max(0, Math.min(1, p.velocity ?? 1));
    const peak = (1 - amount + amount * force) * zone.gain;
    const sustain = peak * p.sustain;
    gain.gain.setValueAtTime(0, when);
    gain.gain.linearRampToValueAtTime(peak, when + p.attack);
    gain.gain.linearRampToValueAtTime(sustain, when + p.attack + p.decay);

    this.triggerFilterEnvelope(when);

    // A non-looping zone plays its region once; the source stops itself at the
    // end of the sample even if the note is held longer, as a sampler should.
    const region = Math.max(0.001, zone.durationSec);
    try {
      if (zone.loop) source.start(when, zone.offsetSec);
      else source.start(when, zone.offsetSec, region);
    } catch {
      gain.disconnect();
      return null;
    }

    const voice: Voice = {
      source,
      gain,
      pitch,
      startedAt: when,
      endsAt: zone.loop ? Infinity : when + region / Math.max(0.01, source.playbackRate.value),
      released: false,
      zone: zone.loop ? zone : null,
      tail: null,
      oneShot: zone.oneShot && !zone.loop,
      chokeGroup: zone.chokeGroup,
      envelope: { startedAt: when, attack: p.attack, decay: p.decay, peak, sustain },
    };
    this.voices.push(voice);
    source.onended = () => this.disposeVoice(voice);
    return voice;
  }

  private triggerFilterEnvelope(when: number): void {
    const p = this.params;
    if (p.filterEnv <= 0) return;
    const depth = p.filterEnv * Math.min(12000, p.cutoff * 6);
    const gain = this.envAmount.gain;
    try {
      gain.cancelScheduledValues(when);
      gain.setValueAtTime(0, when);
      gain.linearRampToValueAtTime(depth, when + Math.max(0.001, p.attack));
      gain.linearRampToValueAtTime(0, when + Math.max(0.002, p.attack + p.decay));
    } catch {
      gain.value = 0;
    }
  }

  private releaseVoice(voice: Voice, when: number, releaseOverride?: number): void {
    if (voice.released && when >= voice.endsAt) return;
    const release = releaseOverride ?? this.params.release;
    const end = when + Math.max(0.005, release);

    voice.released = true;
    voice.endsAt = Math.min(voice.endsAt, end);

    try {
      voice.gain.gain.cancelScheduledValues(when);
      // From the level the envelope will have reached by then — see envelope.ts
      // for why that is worked out rather than read.
      const from = envelopeLevelAt(voice.envelope, when);
      voice.envelope.release = { at: when, from, end };
      voice.gain.gain.setValueAtTime(from, when);
      voice.gain.gain.linearRampToValueAtTime(0, end);
      voice.source.stop(this.startTail(voice, when, end));
    } catch {
      /* the source may already have stopped at the end of its region */
    }
  }

  /**
   * Lets a looping voice out of its loop on release, and says when the loop
   * itself should stop.
   *
   * A zone whose region goes on past its loop — a string's decay, a pad's
   * tail — plays the rest of the region under the release ramp instead of the
   * loop going round under it. A buffer source cannot be told to stop looping
   * at a time, only now, and "now" is ahead of the sound by the scheduler's
   * lookahead; so the tail is a second source, started at the moment the loop
   * stops, from where the loop will have got to by then. Where that is comes
   * from the rate, read once: exact unless the bend is moving, and then the
   * join is a little off under a ramp that is nearly silent by the end.
   */
  private startTail(voice: Voice, when: number, releaseEnd: number): number {
    const zone = voice.zone;
    if (!zone || voice.tail) return releaseEnd;
    const regionEnd = zone.offsetSec + Math.max(0.001, zone.durationSec);
    if (regionEnd - zone.loopEndSec < 0.001) return releaseEnd;

    const rate = Math.max(0.01, voice.source.playbackRate.value) * Math.pow(2, voice.source.detune.value / 1200);
    const elapsed = Math.max(0, when - voice.startedAt) * rate;
    const loopLength = Math.max(0.001, zone.loopEndSec - zone.loopStartSec);
    let position = zone.offsetSec + elapsed;
    if (position > zone.loopEndSec) {
      position = zone.loopStartSec + ((position - zone.loopStartSec) % loopLength);
    }
    const remaining = (regionEnd - position) / rate;
    if (remaining <= 0) return releaseEnd;

    const tail = this.ctx.createBufferSource();
    tail.buffer = voice.source.buffer;
    tail.playbackRate.value = voice.source.playbackRate.value;
    try {
      tail.detune.value = voice.source.detune.value;
      this.bendSource.connect(tail.detune);
    } catch {
      /* as for the voice: no detune, no bend */
    }
    tail.connect(voice.gain);
    tail.start(when, position, regionEnd - position);
    tail.stop(Math.min(releaseEnd, when + remaining));
    // The voice now lives as long as its tail, not its loop.
    voice.source.onended = null;
    tail.onended = () => this.disposeVoice(voice);
    voice.tail = tail;
    return when;
  }

  private disposeVoice(voice: Voice): void {
    const index = this.voices.indexOf(voice);
    if (index >= 0) this.voices.splice(index, 1);
    voice.source.onended = null;
    try {
      voice.source.disconnect();
      voice.gain.disconnect();
      // Disconnecting a node drops what it feeds, never what feeds it, so the
      // bend has to let go of this voice itself. It is the only shared source
      // that reaches a voice: the filter belongs to the instrument here, not to
      // each note, so cutoff and the envelope are connected once and stay.
      this.bendSource.disconnect(voice.source.detune);
      if (voice.tail) {
        voice.tail.onended = null;
        voice.tail.disconnect();
        this.bendSource.disconnect(voice.tail.detune);
      }
    } catch {
      /* already disconnected, or this implementation has no detune */
    }
  }

  /**
   * Forgets finished voices so they stop counting against polyphony, without
   * disconnecting them — an offline render schedules everything before any of
   * it has played, so tearing nodes down here would delete notes from the file.
   */
  private reap(now: number): void {
    for (let i = this.voices.length - 1; i >= 0; i--) {
      if (this.voices[i].endsAt < now) this.voices.splice(i, 1);
    }
  }

  /**
   * A copy of the audio with the zone's loop crossfade written in, when the
   * zone has one it can use; the audio as it is otherwise. Cached by what the
   * blend depends on, so dragging a loop point rebuilds it once per position
   * — an O(file) copy, like the reversed one, and for the same reason: a
   * buffer source plays what it is given and cannot blend as it goes.
   */
  private loopedBuffer(zone: SamplerZone, source: AudioBuffer): AudioBuffer {
    const crossfade = usableCrossfade(zone.loopStartSec, zone.loopEndSec, zone.loopCrossfadeSec);
    if (crossfade <= 0) return source;
    const key = `${zone.assetId}:${zone.reverse ? 'r' : 'f'}:${zone.loopStartSec}:${zone.loopEndSec}:${crossfade}`;
    const cached = this.looped.get(key);
    if (cached && cached.length === source.length) return cached;
    // One prepared copy per zone at a time: a drag through many positions
    // must not keep a copy of the file for each.
    for (const existing of this.looped.keys()) {
      if (existing.startsWith(`${zone.assetId}:`)) this.looped.delete(existing);
    }
    const copy = crossfadedLoopCopy(this.ctx, source, zone.loopStartSec, zone.loopEndSec, crossfade);
    this.looped.set(key, copy);
    return copy;
  }

  /** Builds and caches a reversed copy of an asset's audio. */
  private reversedBuffer(assetId: string, source: AudioBuffer): AudioBuffer {
    const cached = this.reversed.get(assetId);
    if (cached && cached.length === source.length) return cached;

    const copy = this.ctx.createBuffer(source.numberOfChannels, source.length, source.sampleRate);
    for (let c = 0; c < source.numberOfChannels; c++) {
      const from = new Float32Array(source.length);
      source.copyFromChannel(from, c);
      from.reverse();
      copy.copyToChannel(from, c);
    }
    this.reversed.set(assetId, copy);
    return copy;
  }

  dispose(): void {
    for (const voice of [...this.voices]) this.disposeVoice(voice);
    this.reversed.clear();
    this.looped.clear();
    try {
      this.cutoffSource.stop();
      this.modSource.stop();
      this.envSource.stop();
    } catch {
      /* not started, or already stopped */
    }
    this.cutoffSource.disconnect();
    this.modSource.disconnect();
    this.envSource.disconnect();
    this.envAmount.disconnect();
    this.filter.disconnect();
    this.output.disconnect();
  }
}

registerInstrument('sampler', (ctx, resolveBuffer) => new Sampler(ctx, resolveBuffer));
